import { useId, useState } from "react";
import { Check, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGE_LIST, useLanguage } from "@/i18n";
import { cn } from "@/lib/utils";

/**
 * Language switcher.
 *
 * Three placements are supported by the same component, because the switcher
 * has to be reachable from every surface and none of them can be the only one:
 *
 *   - `variant="menu"`  — the avatar dropdown (any language, signed in or out)
 *   - `variant="row"`   — the account settings panel, with the explanation of
 *                         what the setting does *not* change
 *   - `variant="compact"` — the signed-out landing header
 *
 * ## A Popover-ish DropdownMenu, but not an AssistantFab-style popover
 *
 * Unlike the mascot's option menu (which must be a non-modal popover because a
 * Radix menu opens on pointerdown and could open *and* close inside one frame),
 * this trigger is a plain button in a menu of its own, so the dropdown is safe
 * here. Copying the FAB's reasoning would be cargo cult; the two have
 * different failure modes.
 *
 * ## Why the endonym
 *
 * Each option is labelled with its own name — "Tiếng Việt", not "Vietnamese".
 * A reader looking for their language is scanning for the string they know, and
 * an English label is a second translation step for exactly the readers who
 * most need this control. The English name is kept in `aria-label` so assistive
 * tech still announces something unambiguous.
 */
export interface LanguageSwitcherProps {
  variant?: "menu" | "row" | "compact";
  className?: string;
}

export function LanguageSwitcher({ variant = "menu", className }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const languageState = useLanguage();
  const [open, setOpen] = useState(false);
  const labelId = useId();

  // Outside a provider (isolated component tests, or a subtree rendered before
  // LanguageProvider mounts) fall back to whatever i18next resolved. Render
  // rather than throw — a missing provider must not take the nav down with it.
  const language = languageState?.language ?? (i18n.resolvedLanguage as "en" | "vi") ?? "en";
  const setLanguage = languageState?.setLanguage;
  const active = LANGUAGE_LIST.find((entry) => entry.code === language) ?? LANGUAGE_LIST[0];

  const choose = (code: typeof active.code) => {
    setOpen(false);
    setLanguage?.(code);
  };

  const options = (
    <>
      <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Globe className="h-3.5 w-3.5" aria-hidden="true" />
        {t("settings.language.label", "Display language")}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {LANGUAGE_LIST.map((entry) => {
        const isActive = entry.code === language;
        return (
          <DropdownMenuItem
            key={entry.code}
            onClick={() => choose(entry.code)}
            // A checked row is the only non-colour signal of "this is the one
            // you are on"; the tick sits outside the translated label so it
            // cannot collide with longer Vietnamese strings.
            aria-current={isActive}
            className="cursor-pointer gap-2"
          >
            <span aria-hidden="true" className="text-base leading-none">
              {entry.flag}
            </span>
            <span className="flex-1 truncate">{entry.endonym}</span>
            {isActive && <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />}
          </DropdownMenuItem>
        );
      })}
    </>
  );

  if (variant === "compact") {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("settings.language.label", "Display language")}
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm",
              "text-muted-foreground transition-colors hover:bg-stone-100 hover:text-foreground",
              "dark:hover:bg-stone-800",
              "outline-none focus-visible:ring-2 focus-visible:ring-ring",
              className,
            )}
          >
            <Globe className="h-4 w-4" aria-hidden="true" />
            <span aria-hidden="true">{active.flag}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {options}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === "row") {
    return (
      <div className={cn("space-y-2", className)}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-labelledby={labelId}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left",
            "transition-colors hover:bg-stone-100 dark:hover:bg-stone-800",
            "outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Globe className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span id={labelId} className="block text-sm font-semibold">
              {t("settings.language.title", "Language")}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {t("settings.language.description", "Choose the language the app is displayed in.")}
            </span>
          </span>
          <span aria-hidden="true" className="shrink-0 text-base">
            {active.flag}
          </span>
        </button>
        {open && (
          <div className="overflow-hidden rounded-lg border border-stone-200/80 p-1 dark:border-stone-800">
            <div className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
              <Globe className="h-3.5 w-3.5" aria-hidden="true" />
              {t("settings.language.label", "Display language")}
            </div>
            <div className="my-1 h-px bg-stone-200/80 dark:bg-stone-800" />
            {LANGUAGE_LIST.map((entry) => {
              const isActive = entry.code === language;
              return (
                <button
                  key={entry.code}
                  type="button"
                  onClick={() => choose(entry.code)}
                  aria-current={isActive}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                    "hover:bg-stone-100 dark:hover:bg-stone-800",
                    "outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive && "bg-stone-50 dark:bg-stone-800/50",
                  )}
                >
                  <span aria-hidden="true" className="text-base leading-none">
                    {entry.flag}
                  </span>
                  <span className="flex-1 truncate">{entry.endonym}</span>
                  {isActive && (
                    <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-stone-600",
            "transition-colors hover:bg-stone-100 hover:text-stone-900 focus:bg-stone-100 focus:text-stone-900",
            "dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100",
            "outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <Globe className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate text-left">{active.endonym}</span>
          <span aria-hidden="true" className="text-sm">
            {active.flag}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {options}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;